import RoleGroupUsersDisplay from "./parts/RoleGroupUsersDisplay";
import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import MainDetailsDisplay from "./parts/MainDetailsDisplay";
import {
  AsyncCallArgs,
  CircularLoader,
  GridContainer,
  useAsyncCall,
  ViewDialog,
  SimpleConfirmDialog,
  AsyncAutocompleteWithAdd,
  FormSection,
} from "@duosoftbg/nacid-components";
import UsersDialogList from "../../users/dialog/parts/UsersDialogList";
import {
  addUserToRole,
  getFullRoleDetails,
  getUsersWithAdditionalParamsAutocomplete,
  removeUserFromRole,
} from "../../../../../axios/api/services";
import { toast } from "react-toastify";
import useAppDispatch from "../../../../../hooks/redux/base/useAppDispatch";
import useAppSelector from "../../../../../hooks/redux/base/useAppSelector";
import { closeModal } from "../../../../../store/redux/slice/ComponentsControl/rolesControl";
import ScrollableGridContainer from "../../../common/grid/ScrollableGridContainer";

const RoleEditDialog = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { asyncCall } = useAsyncCall();

  const { open, id } = useAppSelector((state) => {
    return state.ComponentsControl.rolesControl.modals.edit;
  });

  const [role, setRole] = useState(null);
  const [users, setUsers] = useState([]);
  const [removeUser, setRemoveUser] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);

  useEffect(() => {
    if (open && id) {
      const getRoleUsersCallArgs: AsyncCallArgs = {
        promise: getFullRoleDetails(id),
        onSuccess: (response) => {
          setRole(response);
          setUsers(response.users);
        },
        onError: () => {
          setRole(null);
          dispatch(closeModal({ modalType: "edit" }));
        },
      };
      asyncCall(getRoleUsersCallArgs);
    }
  }, [open, id, asyncCall, dispatch, t]);

  const handleAddUserToRole = (user) => {
    if (!users.find((arrayUser) => arrayUser.id === user.id)) {
      const addUserCallArgs: AsyncCallArgs = {
        promise: addUserToRole(role.role.id, user.id),
        onSuccess: (response) => {
          if (response) {
            const newUsers = [...users, user];
            setUsers(newUsers);
            toast.success(t("m.role.user.add.success"));
          }
        },
      };
      asyncCall(addUserCallArgs);
    }
  };

  const handleRemoveClick = (user) => {
    setRemoveUser(user);
    setConfirmModalOpen(true);
  };

  const handleRemoveUserFromRole = () => {
    const removeUserCallArgs: AsyncCallArgs = {
      promise: removeUserFromRole(role.role.id, removeUser.id),
      onSuccess: (response) => {
        if (response) {
          const newUsers = users.filter((listUser) => removeUser.id !== listUser.id);
          setUsers(newUsers);
          setRemoveUser(null);
          setConfirmModalOpen(false);
          toast.success(t("m.role.user.remove.success"));
        }
      },
    };
    asyncCall(removeUserCallArgs);
  };

  return (
    <>
      <SimpleConfirmDialog
        open={confirmModalOpen}
        setOpen={setConfirmModalOpen}
        dialogTitleText={"m.confirm"}
        alertText={"m.role.user.remove.confirm"}
        alertType={"warning"}
        onConfirm={handleRemoveUserFromRole}
      />
      <ViewDialog open={open} onClose={() => dispatch(closeModal({ modalType: "edit" }))} title={"t.role.edit.dialog"}>
        {!role && <CircularLoader />}
        {role && (
          <>
            <MainDetailsDisplay record={role.role} />

            <FormSection label={"t.role.users"}>
              <GridContainer spacing={3} mt={0}>
                <AsyncAutocompleteWithAdd
                  key={`users-autocomplete-key-${users.length}`}
                  onAddRecord={handleAddUserToRole}
                  setOptionText={(option) => option.username + " (" + option.fullName + ")"}
                  getOptionLabel={(option) => option.id}
                  setInputOnSelect={(option) => option.username}
                  autocompleteFn={getUsersWithAdditionalParamsAutocomplete}
                  label={"l.select.user"}
                  additionalParams={{ excludedRoleId: role.role.id }}
                  reduceOptionObject={false}
                />
              </GridContainer>

              <ScrollableGridContainer>
                <UsersDialogList users={users} isEdit={true} onRemove={handleRemoveClick} />
              </ScrollableGridContainer>
            </FormSection>

            <RoleGroupUsersDisplay role={role} />
          </>
        )}
      </ViewDialog>
    </>
  );
};

export default RoleEditDialog;
